import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { apiUrl } from './apiUrl';
import { toast } from '@ohif/ui-next';
import { DicomMetadataStore } from '@ohif/core';

// Estación de informe del botón "Reporte" de la lista de estudios del visor.
//
// - Pestaña "Informe": editor por secciones (Técnica, Hallazgos, Impresión
//   diagnóstica, Recomendaciones) con dictado por voz continuo, plantillas del
//   RIS filtradas por modalidad y vista en vivo de la hoja impresa. Guarda en el
//   mismo RisReport que el panel lateral "Reporte Clínico (RIS)".
// - Pestaña "Archivos": subir el PDF/imagen de un informe hecho fuera (lo que
//   hacía antes este botón).
//
// Los estilos van en un <style> propio (prefijo .ei-) para no depender del
// escaneo de Tailwind del build.

// ─── Sesión ──────────────────────────────────────────────────────────────────
function leerSesion() {
  try {
    return JSON.parse(localStorage.getItem('usuario') || 'null');
  } catch {
    return null;
  }
}

function nombreDelToken(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)).nombre || '';
  } catch {
    return '';
  }
}

const cabeceras = () => {
  const s = leerSesion();
  return {
    'Content-Type': 'application/json',
    ...(s?.token ? { Authorization: `Bearer ${s.token}` } : {}),
  };
};

// ─── Contenido del informe (compartido con el panel lateral) ──────────────────
import {
  SECCIONES,
  ordenarPorEstudio,
  coincideConEstudio,
  revisarInforme,
  escapar,
  textoPlano,
  separarSecciones,
  unirSecciones,
} from './revisionInforme';
export { SECCIONES, separarSecciones, unirSecciones, revisarInforme, ordenarPorEstudio };

// Color de cada sección del editor (borde y número)
// En la familia del tema del visor (turquesa); recomendaciones en ámbar porque es opcional
const COLOR_SECCION = { tecnica: '#0e7490', hallazgos: '#008a8a', impresion: '#15803d', recomendaciones: '#b45309' };

// ─── Dictado ─────────────────────────────────────────────────────────────────
// Comandos que se dicen solos (la frase completa es el comando)
const COMANDOS_SOLOS = [
  [/^(siguiente|próxima|proxima) secci[oó]n$/, 'siguiente'],
  [/^secci[oó]n (anterior|previa)$/, 'anterior'],
  [/^(borrar|borra|deshacer) (eso|lo último|lo ultimo)$/, 'borrar'],
  [/^(detener|terminar|parar|fin del?) dictado$/, 'detener'],
  ...SECCIONES.map(s => [new RegExp(`^(ir a |sección |seccion )?${s.titulo.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()}$`), `ir:${s.id}`]),
];

// Puntuación dictada dentro de la frase
const PUNTUACION = [
  [/\s*\b(punto y aparte|punto aparte|nuevo p[aá]rrafo)\b\s*/gi, '.\n'],
  [/\s*\b(nueva l[ií]nea|salto de l[ií]nea)\b\s*/gi, '\n'],
  [/\s*\bpunto y coma\b\s*/gi, '; '],
  [/\s*\bpunto seguido\b\s*/gi, '. '],
  [/\s*\bdos puntos\b\s*/gi, ': '],
  [/\s*\bpuntos suspensivos\b\s*/gi, '… '],
  [/\s*\bpunto\b\s*/gi, '. '],
  [/\s*\bcoma\b\s*/gi, ', '],
  [/\s*\babrir par[eé]ntesis\b\s*/gi, ' ('],
  [/\s*\bcerrar par[eé]ntesis\b\s*/gi, ') '],
  [/\s*\bguion\b\s*/gi, ' - '],
];

const sinTildes = s => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

// Convierte lo dictado en { comando } o { texto, parrafos[] } listo para insertar.
// `continuaFrase` = el texto previo de la sección no terminó en punto.
export function procesarDictado(transcripcion, continuaFrase = false) {
  const limpio = transcripcion.trim().toLowerCase();
  const sinAcentos = sinTildes(limpio);
  for (const [re, comando] of COMANDOS_SOLOS) {
    if (re.test(limpio) || re.test(sinAcentos)) return { comando };
  }
  let texto = ' ' + transcripcion.trim() + ' ';
  for (const [re, signo] of PUNTUACION) texto = texto.replace(re, signo);
  texto = texto.replace(/\s+([.,;:)…])/g, '$1').replace(/\(\s+/g, '(').replace(/[ \t]+/g, ' ');
  // Mayúscula al inicio de cada oración
  const parrafos = texto
    .split('\n')
    .map(p => p.trim())
    .map((p, i) =>
      p.replace(/(^|[.…]\s+)([a-záéíóúñü])/g, (m, antes, letra, pos) =>
        pos === 0 && i === 0 && continuaFrase ? m : antes + letra.toUpperCase()
      )
    );
  return { parrafos };
}

const Reconocimiento = typeof window !== 'undefined' ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition : null;

// ─── Datos DICOM ─────────────────────────────────────────────────────────────
const nombreDicomCrudo = pn => (Array.isArray(pn) ? pn[0]?.Alphabetic : typeof pn === 'string' ? pn : pn?.Alphabetic) || '';
const nombreDicom = pn =>
  nombreDicomCrudo(pn).split('^').filter(Boolean).reverse().join(' ').replace(/\s+/g, ' ').trim();
const fechaDicom = d => (d && d.length === 8 ? `${d.slice(6, 8)}/${d.slice(4, 6)}/${d.slice(0, 4)}` : '');
function edadDesde(nac, ref) {
  if (!nac || nac.length !== 8) return '';
  const n = new Date(+nac.slice(0, 4), +nac.slice(4, 6) - 1, +nac.slice(6, 8));
  const r = ref?.length === 8 ? new Date(+ref.slice(0, 4), +ref.slice(4, 6) - 1, +ref.slice(6, 8)) : new Date();
  let e = r.getFullYear() - n.getFullYear();
  if (r < new Date(r.getFullYear(), n.getMonth(), n.getDate())) e--;
  return e >= 0 ? `${e} años` : '';
}
const SEXO = { M: 'Masculino', F: 'Femenino', O: 'Otro' };
const TITULOS = {
  CT: 'INFORME TOMOGRÁFICO',
  MR: 'INFORME DE RESONANCIA MAGNÉTICA',
  US: 'INFORME ECOGRÁFICO',
  MG: 'INFORME MAMOGRÁFICO',
  CR: 'INFORME RADIOLÓGICO',
  DX: 'INFORME RADIOLÓGICO',
};

// Estudio del PACS (QIDO, DICOM JSON) con los mismos nombres que una instancia
const desdeQido = ds => {
  const v = tag => ds?.[tag]?.Value?.[0];
  return {
    PatientName: v('00100010'),
    PatientID: v('00100020'),
    PatientBirthDate: v('00100030'),
    PatientSex: v('00100040'),
    StudyDate: v('00080020'),
    StudyDescription: v('00081030'),
    AccessionNumber: v('00080050'),
    ReferringPhysicianName: v('00080090'),
    InstitutionName: v('00080080'),
  };
};

// Los datos del paciente salen de la primera imagen cargada; si el visor todavía no
// cargó ninguna (se abrió el informe desde la lista), del estudio consultado al PACS.
function datosDelEstudio(studyMeta, qido = null) {
  const series = studyMeta?.series || [];
  const inst = series.find(s => s.instances?.length)?.instances?.[0] || qido || {};
  const modalidades = [...new Set(series.map(s => s.Modality || s.instances?.[0]?.Modality).filter(Boolean))].filter(
    m => !['SR', 'PR', 'KO', 'SEG'].includes(m)
  );
  return {
    paciente: nombreDicom(inst.PatientName) || 'Paciente sin nombre',
    documento: inst.PatientID || '',
    edad: edadDesde(inst.PatientBirthDate, inst.StudyDate),
    sexo: SEXO[inst.PatientSex] || '',
    fecha: fechaDicom(inst.StudyDate),
    estudio: inst.StudyDescription || inst.ProtocolName || '',
    accession: inst.AccessionNumber || '',
    solicitante: nombreDicom(inst.ReferringPhysicianName),
    institucionDicom: inst.InstitutionName || '',
    equipo: [inst.Manufacturer, inst.ManufacturerModelName].filter(Boolean).join(' '),
    modalidades,
    series: series.length,
    imagenes: series.reduce((n, s) => n + (s.instances?.length || 0), 0),
    // Los archivos subidos se guardan con el nombre DICOM crudo ("APELLIDO^NOMBRE")
    nombreArchivos: nombreDicomCrudo(inst.PatientName),
  };
}

async function consultarPacs(uid) {
  try {
    const fuente = ((window as any).config?.dataSources || []).find(f => f.configuration?.qidoRoot);
    const raiz = fuente?.configuration?.qidoRoot;
    if (!raiz) return null;
    const res = await fetch(`${raiz}/studies?StudyInstanceUID=${encodeURIComponent(uid)}&includefield=all&limit=1`, {
      headers: { Accept: 'application/dicom+json' },
    });
    if (!res.ok || res.status === 204) return null;
    return (await res.json())?.[0] || null;
  } catch {
    return null;
  }
}

const normalizePatientName = (rawName = '') =>
  String(rawName)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\^,]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// ─── Hoja impresa (formato clásico hospitalario, igual al del backend) ───────
// "06/10/2026" -> "6 de octubre de 2026", como en la plantilla Word del servicio
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const fechaLarga = f => {
  const m = (f || '').match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return m ? `${+m[1]} de ${MESES[+m[2] - 1]} de ${m[3]}` : f;
};

// Hoja impresa: copia literal de htmlInforme de RIS-SERVER/src/services/informeDocumento.service.js
// (lo que sale en "Vista previa / PDF" y en Word). Si se cambia allá, copiarla de nuevo aquí.
const srcLogo = logo => (!logo ? null : logo.startsWith('data:') ? logo : `data:image/png;base64,${logo}`);
const fechaHora = f => new Date(f).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' });
// Colores del tema del MED Viewer (Blue-chill, --primary 180 100% 27%): el turquesa
// para líneas y bordes, y uno más oscuro para los títulos, que impreso se lea bien.
const ACENTO = '#008a8a';
const TINTA = '#00585c';
const SANS = 'Arial, Helvetica, sans-serif';

function hojaComun(d, { imprimir = false } = {}) {
  const titulo = TITULOS[d.modalidad] || 'INFORME RADIOLÓGICO';
  const logo = srcLogo(d.logo);
  // Dato del paciente: etiqueta chica arriba, valor abajo
  const dato = (etiqueta, valor, ancho) => `<td style="width:${ancho};padding:7px 10px;vertical-align:top;border-right:1px solid #cfe7e7;">
      <div style="font-family:${SANS};font-size:6.5pt;letter-spacing:.8px;color:#6b7c85;text-transform:uppercase;">${etiqueta}</div>
      <div style="font-family:${SANS};font-size:9.5pt;font-weight:bold;color:#1f2a30;margin-top:2px;">${escapar(valor) || '—'}</div></td>`;
  const edadSexo = [d.edad, d.sexo].filter(Boolean).join(' · ');

  return `<!doctype html><html lang="es"><head><meta charset="utf-8">
<title>${escapar(titulo)} - ${escapar(d.paciente || 'Paciente')}</title>
<style>
  @page { size: A4; margin: 14mm 16mm 16mm; }
  body { font-family: Georgia, "Times New Roman", serif; color: #1f2a30; font-size: 11pt; line-height: 1.55; margin: 0; background: #fff; }
  .hoja { max-width: 760px; margin: 0 auto; padding: 26px 30px; position: relative; }
  .cuerpo h3, .cuerpo h4 { font-family: ${SANS}; font-size: 9.5pt; font-weight: bold; letter-spacing: 1.2px; color: ${TINTA};
    text-transform: uppercase; margin: 16px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #bfe0e0; }
  .cuerpo p { margin: 0 0 6px; text-align: justify; }
  .cuerpo ul, .cuerpo ol { margin: 0 0 6px 20px; padding: 0; }
  .borrador { position: fixed; top: 42%; left: 0; right: 0; text-align: center; font-family: ${SANS}; font-size: min(80pt, 12vw); font-weight: bold;
    letter-spacing: 8px; color: rgba(185,28,28,.07); transform: rotate(-28deg); pointer-events: none; }
  @media print { .hoja { padding: 0; } }
</style></head><body><div class="hoja">
${d.firmado ? '' : '<div class="borrador">BORRADOR</div>'}
<table style="width:100%;border-collapse:collapse;">
  <tr>
    ${logo ? `<td style="width:96px;padding:0 14px 10px 0;vertical-align:middle;"><img src="${logo}" alt="" style="max-width:92px;max-height:64px;"></td>` : ''}
    <td style="padding:0 0 10px;vertical-align:middle;">
      <div style="font-family:${SANS};font-size:15pt;font-weight:bold;color:${TINTA};letter-spacing:.3px;text-transform:uppercase;">${escapar(d.institucion)}</div>
      <div style="font-family:${SANS};font-size:8.5pt;color:#5b6b73;letter-spacing:.4px;margin-top:2px;">Servicio de Imagenología y Diagnóstico por Imágenes</div>
    </td>
  </tr>
</table>
<div style="height:3px;background:${ACENTO};"></div>
<div style="height:1px;background:#7fc6c6;margin-top:2px;"></div>
<p style="text-align:center;font-family:${SANS};font-size:13pt;font-weight:bold;letter-spacing:3px;color:${TINTA};margin:18px 0 2px;">${titulo}</p>
${d.estudio ? `<p style="text-align:center;font-family:${SANS};font-size:9pt;color:#5b6b73;letter-spacing:1px;margin:0 0 14px;text-transform:uppercase;">${escapar(d.estudio)}</p>` : '<div style="height:12px;"></div>'}
<table style="width:100%;border-collapse:collapse;background:#eef8f8;border:1px solid #cfe7e7;border-left:4px solid ${ACENTO};margin-bottom:8px;">
  <tr style="border-bottom:1px solid #cfe7e7;">
    ${dato('Paciente', d.paciente, '40%')}${dato('C.I. / ID', d.documento, '20%')}${dato('Edad / Sexo', edadSexo, '20%')}${dato('Fecha del estudio', d.fechaEstudio, '20%')}
  </tr>
  <tr>
    ${dato('Médico solicitante', d.solicitante, '40%')}${dato('Modalidad', d.modalidad, '20%')}${dato('N.º de orden', d.accession, '20%')}${dato('Médico informante', d.medico, '20%')}
  </tr>
</table>
<div class="cuerpo">${d.cuerpo || '<p><i>Informe sin contenido.</i></p>'}</div>
<table style="width:100%;margin-top:64px;border-collapse:collapse;"><tr><td style="width:45%;"></td>
  <td style="width:55%;text-align:center;font-family:${SANS};">
    <div style="border-top:1px solid #1f2a30;margin:0 30px;padding-top:6px;font-size:10.5pt;font-weight:bold;color:#1f2a30;">${escapar(d.medico || 'Médico radiólogo')}</div>
    <div style="font-size:8.5pt;color:#5b6b73;letter-spacing:.5px;">MÉDICO RADIÓLOGO</div>
    ${d.firmado ? `<div style="display:inline-block;margin-top:6px;padding:2px 10px;border:1px solid #16a34a;border-radius:99px;font-size:7.5pt;color:#15803d;letter-spacing:.5px;">✓ FIRMADO ELECTRÓNICAMENTE${d.firmadoEl ? ` · ${escapar(d.firmadoEl)}` : ''}</div>` : ''}
  </td></tr></table>
<table style="width:100%;margin-top:28px;border-collapse:collapse;border-top:1px solid #cfe7e7;"><tr>
  <td style="padding-top:5px;font-family:${SANS};font-size:7pt;color:#8a979e;">${escapar(d.institucion)} · Servicio de Imagenología</td>
  <td style="padding-top:5px;font-family:${SANS};font-size:7pt;color:#8a979e;text-align:right;">Generado el ${escapar(fechaHora(new Date()))} · Estudio ${escapar(String(d.uid || '').slice(-12))}</td>
</tr></table>
</div>${imprimir ? '<script>window.onload=()=>setTimeout(()=>window.print(),300)</script>' : ''}</body></html>`;
}

function hojaHtml({ d, cuerpo, medico, firmado, firmadoEl, institucion, logo, uid, imprimir = false }) {
  return hojaComun(
    {
      paciente: d.paciente,
      documento: d.documento,
      edad: d.edad,
      sexo: d.sexo,
      estudio: d.estudio,
      modalidad: d.modalidades[0],
      fechaEstudio: fechaLarga(d.fecha),
      accession: d.accession,
      solicitante: d.solicitante,
      medico,
      firmado,
      firmadoEl,
      cuerpo: cuerpo || '<p style="color:#9ca3af;"><i>El informe aparecerá aquí a medida que lo escriba o dicte.</i></p>',
      uid,
      institucion,
      logo,
    },
    { imprimir }
  );
}

// ─── Estilos ─────────────────────────────────────────────────────────────────
const CSS = `
.ei-fondo { position:fixed; inset:0; z-index:60; background:rgba(0,20,22,.86); display:flex; align-items:stretch; justify-content:center; padding:18px; font-family:Inter,"Segoe UI",Roboto,sans-serif; color:#e6f4f3; }
.ei-fondo * { box-sizing:border-box; }
.ei-fondo, .ei-fondo * { scrollbar-width:thin; scrollbar-color:#1f5a5a transparent; }
.ei-fondo ::-webkit-scrollbar { width:8px; height:8px; }
.ei-fondo ::-webkit-scrollbar-track { background:transparent; }
.ei-fondo ::-webkit-scrollbar-thumb { background:#1f5a5a; border-radius:99px; border:2px solid transparent; background-clip:padding-box; }
.ei-fondo ::-webkit-scrollbar-thumb:hover { background:#2b7a7a; background-clip:padding-box; }
.ei-ventana { width:100%; max-width:1500px; background:#00282b; border:1px solid #1f4747; border-radius:14px; display:flex; flex-direction:column; overflow:hidden; box-shadow:0 30px 80px rgba(0,0,0,.6); }
.ei-top { display:flex; align-items:center; gap:14px; padding:12px 18px; border-bottom:1px solid #1f4747; background:linear-gradient(90deg,#003538,#00282b); }
.ei-top h2 { margin:0; font-size:16px; font-weight:650; letter-spacing:.2px; }
.ei-top .sub { color:#8fbcbc; font-size:12px; }
.ei-tabs { display:flex; gap:4px; margin-left:18px; background:#00282b; border:1px solid #1f4747; border-radius:9px; padding:3px; }
.ei-tab { background:transparent; border:0; color:#a8d0d0; padding:6px 14px; border-radius:7px; cursor:pointer; font-size:12.5px; font-weight:600; }
.ei-tab.on { background:#007070; color:#fff; }
.ei-pill { font-size:10.5px; font-weight:700; letter-spacing:.6px; padding:3px 10px; border-radius:99px; text-transform:uppercase; }
.ei-pill.borrador { background:rgba(245,158,11,.14); color:#fbbf24; border:1px solid rgba(245,158,11,.4); }
.ei-pill.firmado { background:rgba(34,197,94,.14); color:#4ade80; border:1px solid rgba(34,197,94,.4); }
.ei-pill.nuevo { background:rgba(63,214,204,.12); color:#3fd6cc; border:1px solid rgba(63,214,204,.35); }
.ei-x { margin-left:auto; background:transparent; border:1px solid #2a5c5c; color:#c5e3e3; width:34px; height:34px; border-radius:9px; cursor:pointer; font-size:16px; }
.ei-x:hover { background:#0d5454; }
.ei-cuerpo { flex:1; display:grid; grid-template-columns:260px minmax(0,1fr) minmax(0,1fr); min-height:0; }
.ei-col { min-height:0; overflow-y:auto; padding:14px; }
.ei-izq { border-right:1px solid #1f4747; background:#002d30; display:flex; flex-direction:column; gap:12px; }
.ei-der { border-left:1px solid #1f4747; background:#002f32; padding:14px 18px; }
.ei-card { background:#003a3d; border:1px solid #1f4747; border-radius:11px; padding:12px; }
.ei-pac { font-size:15px; font-weight:650; line-height:1.25; }
.ei-dato { display:flex; justify-content:space-between; gap:8px; font-size:12px; padding:4px 0; border-bottom:1px dashed #1f2846; }
.ei-dato:last-child { border-bottom:0; }
.ei-dato span { color:#8fbcbc; }
.ei-dato b { font-weight:600; text-align:right; }
.ei-label { font-size:10.5px; text-transform:uppercase; letter-spacing:.9px; color:#8fbcbc; margin:0 0 8px; display:flex; justify-content:space-between; align-items:center; }
.ei-link { background:none; border:0; color:#3fd6cc; cursor:pointer; font-size:11px; padding:0; }
.ei-input { width:100%; background:#00282b; border:1px solid #2a5c5c; color:#e6f4f3; border-radius:8px; padding:7px 9px; font-size:12.5px; outline:none; }
.ei-input:focus { border-color:#3fd6cc; }
.ei-tpls { display:flex; flex-direction:column; gap:4px; margin-top:8px; max-height:260px; overflow-y:auto; }
.ei-fondo .ei-tpl { text-align:left; background:#003033; border:1px solid #1f4747; color:#d5ecec; border-radius:9px; padding:8px 10px; cursor:pointer; font-size:12.5px; line-height:1.35; display:flex; justify-content:space-between; align-items:flex-start; gap:8px; transition:background .15s, border-color .15s, transform .1s; }
.ei-fondo .ei-tpl:hover { border-color:#3fd6cc; background:#004548; transform:translateX(2px); }
.ei-fondo .ei-tpl small { color:#3fd6cc; font-weight:700; font-size:10.5px; flex:none; padding-top:1px; }
.ei-fondo .ei-tpl.sugerida { border-color:rgba(52,211,153,.45); background:linear-gradient(90deg,rgba(16,185,129,.12),#003033 70%); }
.ei-fondo .ei-tpl.sugerida small { color:#34d399; text-transform:uppercase; letter-spacing:.5px; }
.ei-ayuda { font-size:11.5px; color:#8fbcbc; line-height:1.6; }
.ei-ayuda kbd { background:#0d5454; border:1px solid #2a5c5c; border-radius:4px; padding:0 5px; font-size:10.5px; color:#d9f0ef; }
.ei-centro { display:flex; flex-direction:column; gap:12px; padding:14px 16px; }
.ei-barra { display:flex; flex-wrap:wrap; align-items:center; gap:8px; position:sticky; top:-14px; z-index:2; padding:8px 0; background:linear-gradient(#00282b 80%, rgba(0,40,43,0)); }
.ei-grupo { display:inline-flex; background:#003a3d; border:1px solid #1f4747; border-radius:9px; padding:2px; gap:2px; }
.ei-fondo .ei-tb { min-width:32px; height:30px; background:transparent; border:0; color:#c5e3e3; border-radius:7px; cursor:pointer; font-size:12.5px; font-weight:600; padding:0 10px; transition:background .12s, color .12s; }
.ei-fondo .ei-tb:hover { background:#007070; color:#fff; }
.ei-sec { --sc:#0891b2; position:relative; background:#fdfdfb; border-radius:12px; border:1px solid #e5e7eb; overflow:hidden; box-shadow:0 1px 2px rgba(0,0,0,.25), 0 6px 18px rgba(0,0,0,.18); transition:box-shadow .15s; }
.ei-sec::before { content:''; position:absolute; left:0; top:0; bottom:0; width:4px; background:var(--sc); }
.ei-sec.activa { box-shadow:0 0 0 2px var(--sc), 0 8px 24px rgba(0,0,0,.3); }
.ei-sec.dictando { box-shadow:0 0 0 2px #ef4444, 0 0 0 6px rgba(239,68,68,.18); }
.ei-sec-head { display:flex; align-items:center; gap:9px; padding:10px 14px 2px 18px; }
.ei-sec-head .num { width:20px; height:20px; border-radius:50%; background:var(--sc); color:#fff; font-size:11px; font-weight:700; display:flex; align-items:center; justify-content:center; flex:none; }
.ei-sec-head h3 { margin:0; font-size:11.5px; letter-spacing:1.3px; text-transform:uppercase; color:var(--sc); font-weight:750; flex:1; }
.ei-fondo .ei-mic { border:0; border-radius:99px; padding:5px 12px; font-size:11.5px; font-weight:650; cursor:pointer; display:flex; align-items:center; gap:6px; background:#ecfeff; color:#008a8a; }
.ei-fondo .ei-mic:hover { background:#cffafe; }
.ei-mic.on { background:#ef4444; color:#fff; }
.ei-mic.on i { width:8px; height:8px; border-radius:50%; background:#fff; animation:ei-p 1s infinite; }
@keyframes ei-p { 50% { opacity:.25; } }
.ei-edit { min-height:70px; padding:6px 18px 14px; color:#1f2937; font-family:Georgia,"Times New Roman",serif; font-size:14px; line-height:1.6; outline:none; }
.ei-edit p { margin:0 0 6px; }
.ei-edit ul, .ei-edit ol { margin:2px 0 6px 20px; padding:0; }
.ei-edit:empty:before { content:attr(data-placeholder); color:#9ca3af; font-style:italic; }
.ei-edit[contenteditable=false] { color:#374151; }
.ei-vivo { margin:0 12px 10px; padding:6px 10px; border-radius:8px; background:#fef2f2; color:#991b1b; font-size:12.5px; font-style:italic; }
.ei-hoja-wrap { background:radial-gradient(ellipse at top,#2a3350,#161c30 70%); border:1px solid #1f4747; border-radius:12px; padding:20px; display:flex; justify-content:center; }
.ei-hoja { display:block; width:100%; max-width:720px; aspect-ratio:210/297; background:#fff; border:0; border-radius:2px; box-shadow:0 2px 4px rgba(0,0,0,.3), 0 18px 40px rgba(0,0,0,.45); }
.ei-pie { display:flex; align-items:center; gap:10px; padding:12px 18px; border-top:1px solid #1f4747; background:#002d30; }
.ei-pie .meta { color:#8fbcbc; font-size:12px; }
.ei-pie .sucio { color:#fbbf24; }
.ei-btn { border:0; border-radius:9px; padding:10px 16px; font-size:13px; font-weight:650; cursor:pointer; transition:filter .15s, background .15s; }
.ei-btn:disabled { opacity:.5; cursor:default; }
.ei-btn.sec { background:#0d5454; color:#d9f0ef; }
.ei-btn.sec:hover:not(:disabled) { background:#0f6363; }
.ei-btn.pri { background:linear-gradient(135deg,#16a34a,#15803d); color:#fff; }
.ei-btn.pri:hover:not(:disabled) { filter:brightness(1.1); }
.ei-btn.peligro { background:transparent; color:#fca5a5; border:1px solid rgba(248,113,113,.4); }
.ei-der h4 { margin:0 0 10px; font-size:10.5px; letter-spacing:.9px; text-transform:uppercase; color:#8fbcbc; font-weight:600; display:flex; justify-content:space-between; align-items:center; }
.ei-a4 { font-size:10px; letter-spacing:.5px; color:#a8d0d0; border:1px solid #2a5c5c; border-radius:5px; padding:1px 6px; }
.ei-archivos { padding:22px; max-width:760px; margin:0 auto; width:100%; display:flex; flex-direction:column; gap:14px; }
.ei-drop { border:2px dashed #2a5c5c; border-radius:12px; padding:26px; text-align:center; color:#a8d0d0; cursor:pointer; background:#003033; }
.ei-drop:hover { border-color:#3fd6cc; }
.ei-fila-arch { display:flex; align-items:center; gap:10px; background:#003a3d; border:1px solid #1f4747; border-radius:9px; padding:9px 12px; font-size:13px; }
.ei-fila-arch span { flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.ei-mini { border:0; border-radius:6px; padding:5px 9px; font-size:11.5px; cursor:pointer; font-weight:600; }
.ei-confirm { position:absolute; inset:0; background:rgba(0,20,22,.7); display:flex; align-items:center; justify-content:center; z-index:5; }
.ei-confirm > div { background:#003a3d; border:1px solid #2a5c5c; border-radius:12px; padding:18px; max-width:380px; }
.ei-confirm > div.ancho { max-width:560px; width:calc(100% - 32px); max-height:80vh; overflow:auto; }
.ei-rev { list-style:none; margin:4px 0 14px; padding:0; display:flex; flex-direction:column; gap:6px; }
.ei-rev li { display:flex; gap:10px; align-items:flex-start; padding:9px 11px; border-radius:9px; cursor:pointer; font-size:12.5px; line-height:1.45; color:#d9f0ef; background:#003f42; border-left:3px solid #64748b; }
.ei-rev li:hover { filter:brightness(1.15); }
.ei-rev li b { flex:0 0 64px; font-size:10.5px; text-transform:uppercase; letter-spacing:.6px; padding-top:1px; }
.ei-rev li.critico { border-left-color:#ef4444; background:rgba(239,68,68,.12); } .ei-rev li.critico b { color:#fca5a5; }
.ei-rev li.error { border-left-color:#f59e0b; } .ei-rev li.error b { color:#fcd34d; }
.ei-rev li.aviso { border-left-color:#38bdf8; } .ei-rev li.aviso b { color:#7dd3fc; }
.ei-rev li.guia { border-left-color:#a78bfa; } .ei-rev li.guia b { color:#c4b5fd; }
.ei-rev-ok { color:#86efac !important; font-weight:600; }
.ei-confirm h3 { margin:0 0 6px; font-size:15px; }
.ei-confirm p { margin:0 0 16px; color:#a8d0d0; font-size:13px; line-height:1.5; }
/* Botón de la columna de series: angosta, por eso en dos líneas (antes la etiqueta se cortaba: "BORRA") */
.ei-boton { display:flex; flex-direction:column; align-items:flex-start; gap:4px; width:100%; max-width:100%; box-sizing:border-box; overflow:hidden;
  border:1px solid rgba(255,255,255,.08); border-left:4px solid var(--c); border-radius:9px; padding:9px 11px; background:linear-gradient(135deg,#00403f,#003336);
  color:#eefaf9; cursor:pointer; text-align:left; transition:filter .15s, transform .15s; box-shadow:0 2px 8px rgba(0,0,0,.25); }
.ei-boton:hover { filter:brightness(1.15); }
.ei-boton:active { transform:scale(.98); }
.ei-boton .t { display:flex; align-items:center; gap:8px; font-size:13.5px; font-weight:650; white-space:nowrap; }
.ei-boton .t svg { color:var(--c); flex:none; }
.ei-boton small { display:flex; align-items:center; gap:6px; font-size:10.5px; font-weight:700; letter-spacing:.7px; text-transform:uppercase; color:var(--c); white-space:nowrap; }
.ei-boton small::before { content:''; width:7px; height:7px; border-radius:50%; background:var(--c); box-shadow:0 0 0 3px color-mix(in srgb, var(--c) 25%, transparent); }
@media (max-width: 1180px) { .ei-cuerpo { grid-template-columns:230px minmax(0,1fr); } .ei-der { display:none; } }
@media (max-width: 760px) { .ei-fondo { padding:0; } .ei-ventana { border-radius:0; } .ei-cuerpo { grid-template-columns:1fr; } .ei-izq { display:none; } }
`;

// ─── Componente ──────────────────────────────────────────────────────────────
function UploadDocuments({
  value = '',
  seriesStudy = '',
  seriesColumn = '',
  temporalView = false,
  instanceUid = '',
  orderId = '',
  onSuccess = () => {},
}: {
  value: string;
  seriesStudy: any;
  seriesColumn: any;
  temporalView: boolean;
  instanceUid?: string;
  orderId?: string;
  onSuccess?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [pestana, setPestana] = useState('informe');
  const [studyMeta, setStudyMeta] = useState<any>(null);

  // Informe
  const [secciones, setSecciones] = useState({ tecnica: '', hallazgos: '', impresion: '', recomendaciones: '' });
  const [report, setReport] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [errorCarga, setErrorCarga] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [sucio, setSucio] = useState(false);
  const [activa, setActiva] = useState('hallazgos');
  const [templates, setTemplates] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [todas, setTodas] = useState(false);
  const [confirmar, setConfirmar] = useState<any>(null);
  const [ajustes, setAjustes] = useState({ institucion: '', logo: '' });

  // Dictado
  const [dictando, setDictando] = useState('');
  const [enVivo, setEnVivo] = useState('');
  const reconRef = useRef<any>(null);
  const dictandoRef = useRef('');
  const ultimoDictadoRef = useRef<{ seccion: string; html: string } | null>(null);
  const editoresRef = useRef<Record<string, HTMLDivElement | null>>({});
  const seccionesRef = useRef(secciones);
  seccionesRef.current = secciones;

  // Archivos
  const [existingFiles, setExistingFiles] = useState([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const inputArchivoRef = useRef<HTMLInputElement | null>(null);

  const sesion = leerSesion();
  const rolUsuario = sesion?.user?.role || '';
  const vistas = sesion?.user?.vistas || [];
  const puedeInformar = rolUsuario === 'admin' || vistas.includes('INFORMES');
  const medico = sesion?.user?.nombre || nombreDelToken(sesion?.token);
  const firmado = report?.status === 'SIGNED';
  const editable = puedeInformar && !firmado;

  const [qido, setQido] = useState<any>(null);
  const d = useMemo(() => datosDelEstudio(studyMeta, qido), [studyMeta, qido]);
  const nombreArchivos = normalizePatientName(d.nombreArchivos);

  // ── Metadatos del estudio (llegan después de montar la lista) ──
  // El visor llena el mismo objeto a medida que carga: se vuelve a leer hasta que
  // tenga imágenes (copia nueva para que React recalcule), y mientras tanto se
  // piden los datos del paciente al PACS.
  useEffect(() => {
    if (!instanceUid) return;
    let intentos = 0;
    let timer;
    let imagenes = -1;
    let pedidoPacs = false;
    const leer = () => {
      const meta = DicomMetadataStore.getStudy(String(instanceUid));
      const n = (meta?.series || []).reduce((t, s) => t + (s.instances?.length || 0), 0);
      if (meta?.series?.length && n !== imagenes) {
        imagenes = n;
        setStudyMeta({ ...meta });
      }
      if (!n && !pedidoPacs) {
        pedidoPacs = true;
        consultarPacs(String(instanceUid)).then(ds => ds && setQido(desdeQido(ds)));
      }
      if (!n && intentos++ < 30) timer = setTimeout(leer, 1000);
    };
    leer();
    return () => clearTimeout(timer);
  }, [instanceUid, isOpen]);

  // ── Archivos subidos del paciente ──
  const fetchExistingFiles = useCallback(async () => {
    if (!nombreArchivos) return;
    setLoadingFiles(true);
    try {
      const res = await fetch(`${apiUrl}/api/subida/files/${encodeURIComponent(nombreArchivos)}`);
      const lista = res.ok ? await res.json() : [];
      setExistingFiles(Array.isArray(lista) ? lista : []);
    } catch {
      setExistingFiles([]);
    } finally {
      setLoadingFiles(false);
    }
  }, [nombreArchivos]);

  useEffect(() => {
    if (nombreArchivos) fetchExistingFiles();
  }, [nombreArchivos, isOpen, temporalView]);

  const hayEstudio = !!studyMeta;

  // ── Informe del estudio (para el estado del botón y al abrir) ──
  const cargarInforme = useCallback(async () => {
    if (!instanceUid) return;
    setCargando(true);
    setErrorCarga('');
    try {
      const res = await fetch(`${apiUrl}/api/ris/reports/${encodeURIComponent(instanceUid)}`, { headers: cabeceras() });
      if (!res.ok) throw new Error(res.status === 401 ? 'La sesión venció: vuelva a iniciar sesión.' : `El servidor respondió ${res.status}.`);
      const data = await res.json();
      setReport(data || null);
      setSecciones(separarSecciones(data?.contentHtml || ''));
      setSucio(false);
    } catch (e) {
      setErrorCarga(e?.message?.startsWith('La sesión') || e?.message?.startsWith('El servidor') ? e.message : 'No hay conexión con el servidor del RIS.');
    } finally {
      setCargando(false);
    }
  }, [instanceUid]);

  useEffect(() => {
    if (hayEstudio) cargarInforme();
  }, [hayEstudio, cargarInforme]);

  useEffect(() => {
    if (!isOpen) return;
    cargarInforme();
    fetch(`${apiUrl}/api/ris/templates`, { headers: cabeceras() })
      .then(r => (r.ok ? r.json() : []))
      .then(setTemplates)
      .catch(() => setTemplates([]));
    fetch(`${apiUrl}/api/settings`)
      .then(r => (r.ok ? r.json() : null))
      .then(s => s && setAjustes({ institucion: s.institution_name || '', logo: s.logo_primary_base64 || '' }))
      .catch(() => {});
  }, [isOpen]);

  // Los editores son contentEditable: se rellenan a mano cuando cambia el
  // contenido desde fuera (carga, plantilla, dictado), no en cada tecla.
  const pintarEditores = useCallback(nuevas => {
    SECCIONES.forEach(s => {
      const el = editoresRef.current[s.id];
      if (el && el.innerHTML !== nuevas[s.id]) el.innerHTML = nuevas[s.id];
    });
  }, []);

  useEffect(() => {
    if (isOpen && pestana === 'informe' && !cargando) pintarEditores(secciones);
  }, [isOpen, pestana, cargando, firmado, report]);

  const ponerSecciones = (nuevas, marcarSucio = true) => {
    // El ref se actualiza ya: varias frases dictadas pueden llegar en el mismo evento
    seccionesRef.current = nuevas;
    setSecciones(nuevas);
    pintarEditores(nuevas);
    if (marcarSucio) setSucio(true);
  };

  const alEditar = id => {
    const html = editoresRef.current[id]?.innerHTML || '';
    setSecciones(prev => ({ ...prev, [id]: html }));
    setSucio(true);
  };

  // ── Plantillas ──
  const plantillasVisibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return ordenarPorEstudio(
      templates.filter(
        t =>
          (todas || !d.modalidades.length || d.modalidades.includes(t.modality)) &&
          (!q || t.name.toLowerCase().includes(q))
      ),
      d.estudio
    );
  }, [templates, busqueda, todas, d.modalidades, d.estudio]);

  const aplicarPlantilla = t => {
    const nuevas = separarSecciones(t.contentHtml);
    const hayTexto = SECCIONES.some(s => textoPlano(secciones[s.id]));
    if (!hayTexto) return ponerSecciones(nuevas);
    setConfirmar({
      titulo: `Usar "${t.name}"`,
      texto: 'Se reemplazará lo que ya está escrito en el informe por el texto de esta plantilla.',
      boton: 'Reemplazar',
      accion: () => ponerSecciones(nuevas),
    });
  };

  // ── Dictado ──
  const insertarDictado = (seccion, parrafos) => {
    const actual = seccionesRef.current[seccion] || '';
    const textoActual = textoPlano(actual);
    let html = actual;
    parrafos.forEach((p, i) => {
      if (!p) return;
      const unir = i === 0 && textoActual && !/[.…:]$/.test(textoActual) && /<\/p>$/.test(html);
      if (unir) html = html.replace(/<\/p>$/, ` ${escapar(p)}</p>`);
      else html += `<p>${escapar(p)}</p>`;
    });
    ultimoDictadoRef.current = { seccion, html: actual };
    const nuevas = { ...seccionesRef.current, [seccion]: html };
    ponerSecciones(nuevas);
  };

  const detenerDictado = useCallback(() => {
    dictandoRef.current = '';
    setDictando('');
    setEnVivo('');
    try {
      reconRef.current?.stop();
    } catch {}
  }, []);

  const iniciarDictado = seccion => {
    if (!Reconocimiento) {
      toast.warning('Este navegador no permite dictado. Use Google Chrome o Microsoft Edge.');
      return;
    }
    if (!editable) return;
    if (dictandoRef.current) detenerDictado();
    const rec = new Reconocimiento();
    rec.lang = 'es-BO';
    rec.continuous = true;
    rec.interimResults = true;
    rec.onresult = ev => {
      let parcial = '';
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        if (!r.isFinal) {
          parcial += r[0].transcript;
          continue;
        }
        const objetivo = dictandoRef.current;
        if (!objetivo) return;
        const continua = !!textoPlano(seccionesRef.current[objetivo]) && !/[.…:]$/.test(textoPlano(seccionesRef.current[objetivo]));
        const res = procesarDictado(r[0].transcript, continua);
        if (res.comando) ejecutarComando(res.comando);
        else insertarDictado(objetivo, res.parrafos);
      }
      setEnVivo(parcial);
    };
    rec.onerror = ev => {
      if (ev.error === 'not-allowed') {
        toast.error('El navegador no dio permiso al micrófono.');
        detenerDictado();
      }
    };
    // Chrome corta el reconocimiento tras un silencio: se reanuda solo
    rec.onend = () => {
      if (dictandoRef.current) {
        try {
          rec.start();
        } catch {}
      }
    };
    reconRef.current = rec;
    dictandoRef.current = seccion;
    setDictando(seccion);
    setActiva(seccion);
    try {
      rec.start();
    } catch {}
  };

  const ejecutarComando = comando => {
    const idx = SECCIONES.findIndex(s => s.id === dictandoRef.current);
    if (comando === 'detener') return detenerDictado();
    if (comando === 'borrar') {
      const u = ultimoDictadoRef.current;
      if (u) {
        ponerSecciones({ ...seccionesRef.current, [u.seccion]: u.html });
        ultimoDictadoRef.current = null;
      }
      return;
    }
    let destino = null;
    if (comando === 'siguiente') destino = SECCIONES[Math.min(idx + 1, SECCIONES.length - 1)].id;
    if (comando === 'anterior') destino = SECCIONES[Math.max(idx - 1, 0)].id;
    if (comando.startsWith('ir:')) destino = comando.slice(3);
    if (destino) {
      dictandoRef.current = destino;
      setDictando(destino);
      setActiva(destino);
      editoresRef.current[destino]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  useEffect(() => () => detenerDictado(), []);
  useEffect(() => {
    if (!isOpen || firmado) detenerDictado();
  }, [isOpen, firmado]);

  // ── Guardar / firmar ──
  const contenidoHtml = useMemo(() => unirSecciones(secciones), [secciones]);

  const guardar = async (status, revertStatus = false) => {
    if (guardando) return false;
    if (status === 'SIGNED' && !textoPlano(contenidoHtml)) {
      toast.warning('El informe está vacío: no se puede firmar.');
      return false;
    }
    if (status === 'SIGNED' && !textoPlano(secciones.impresion)) {
      toast.warning('Faltan las conclusiones.');
      setActiva('impresion');
      return false;
    }
    detenerDictado();
    setGuardando(true);
    try {
      const res = await fetch(`${apiUrl}/api/ris/reports`, {
        method: 'POST',
        headers: cabeceras(),
        body: JSON.stringify({
          studyInstanceUid: instanceUid,
          ...(orderId ? { orderId } : {}),
          contentHtml: contenidoHtml,
          status,
          radiologist: medico || 'Médico imagenólogo',
          revertStatus,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || res.statusText);
      }
      setReport(await res.json());
      setSucio(false);
      toast.success(
        status === 'SIGNED' ? 'Informe firmado y enviado al RIS' : revertStatus ? 'El informe volvió a borrador' : 'Borrador guardado'
      );
      onSuccess();
      return true;
    } catch (e) {
      toast.error(`No se pudo guardar: ${e.message}`);
      return false;
    } finally {
      setGuardando(false);
    }
  };

  // Revisión automática: al firmar (si hay observaciones el médico decide) o a pedido
  const abrirRevision = paraFirmar => {
    const obs = revisarInforme(secciones, d);
    const textoFirma = `Se firmará a nombre de ${medico || 'el usuario actual'} y se enviará al RIS. Después no se podrá editar sin volverlo a borrador.`;
    if (paraFirmar && !obs.length) {
      setConfirmar({
        titulo: 'Firmar informe',
        texto: textoFirma,
        nota: '✓ Revisión automática: sin observaciones.',
        boton: 'Firmar',
        accion: () => guardar('SIGNED'),
      });
      return;
    }
    setConfirmar({
      titulo: paraFirmar ? 'Revisión antes de firmar' : 'Revisión del informe',
      texto: obs.length
        ? `${obs.length} ${obs.length === 1 ? 'observación' : 'observaciones'}. Toque una para ir a la sección.`
        : 'Sin observaciones: no hay campos sin completar, incoherencias ni hallazgos críticos.',
      lista: obs,
      cancelar: paraFirmar ? 'Corregir' : 'Cerrar',
      boton: paraFirmar ? 'Firmar de todos modos' : null,
      accion: paraFirmar ? () => guardar('SIGNED') : null,
    });
  };
  const irASeccion = id => {
    setConfirmar(null);
    setActiva(id);
    setTimeout(() => editoresRef.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);
  };

  // Atajos: Ctrl+S guarda, Ctrl+Espacio enciende o apaga el micrófono
  useEffect(() => {
    if (!isOpen) return;
    const tecla = e => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's' && editable) {
        e.preventDefault();
        guardar('DRAFT');
      }
      if (e.ctrlKey && e.code === 'Space' && editable) {
        e.preventDefault();
        dictandoRef.current ? detenerDictado() : iniciarDictado(activa);
      }
      if (e.key === 'Escape' && !confirmar) cerrar();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  });

  const datosHoja = (imprimir = false) => ({
    d,
    cuerpo: contenidoHtml,
    medico: report?.radiologist || medico,
    firmado,
    firmadoEl: report?.signedAt ? new Date(report.signedAt).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' }) : '',
    institucion: ajustes.institucion || d.institucionDicom || 'Servicio de Imagenología',
    logo: ajustes.logo,
    uid: instanceUid,
    imprimir,
  });

  const imprimir = async () => {
    const ventana = window.open('', '_blank');
    if (!ventana) return toast.warning('El navegador bloqueó la ventana. Permita ventanas emergentes para este sitio.');
    if (sucio && editable) await guardar('DRAFT');
    try {
      // Si el backend ya tiene la hoja (misma que el PDF del RIS), se usa esa
      const res = await fetch(`${apiUrl}/api/ris/reports/${encodeURIComponent(instanceUid)}/documento?formato=pdf`, { headers: cabeceras() });
      if (res.ok) {
        ventana.document.write(await res.text());
        ventana.document.close();
        return;
      }
    } catch {}
    ventana.document.write(hojaHtml(datosHoja(true)));
    ventana.document.close();
  };

  const cerrar = () => {
    if (sucio && editable) {
      setConfirmar({
        titulo: 'Cambios sin guardar',
        texto: 'El informe tiene cambios que no se guardaron. ¿Guardarlos como borrador antes de cerrar?',
        boton: 'Guardar y cerrar',
        secundario: 'Cerrar sin guardar',
        accion: async () => (await guardar('DRAFT')) && setIsOpen(false),
        accionSecundaria: () => {
          setSucio(false);
          setIsOpen(false);
        },
      });
      return;
    }
    detenerDictado();
    setIsOpen(false);
  };

  const formato = (cmd, valor = null) => {
    const el = editoresRef.current[activa];
    if (!el || !editable) return;
    el.focus();
    document.execCommand(cmd, false, valor);
    alEditar(activa);
  };

  // Al pegar desde Word u otra web se pega solo el texto
  const alPegar = e => {
    e.preventDefault();
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'));
  };

  // ── Subida de archivos (flujo anterior) ──
  const subirArchivo = async () => {
    if (!archivo) return toast.warning('Seleccione un archivo para subir');
    if (!nombreArchivos) return toast.warning('Todavía no se leyó el nombre del paciente');
    setSubiendo(true);
    const formData = new FormData();
    formData.append('file', archivo);
    formData.append('paciente', nombreArchivos);
    try {
      const res = await fetch(`${apiUrl}/api/subida/upload`, { method: 'POST', body: formData });
      if (!res.ok) throw new Error(`Error ${res.status}: ${await res.text()}`);
      const result = await res.json();
      // Si no hay informe escrito, el archivo subido cuenta como informe firmado
      if (!textoPlano(report?.contentHtml || '')) {
        await fetch(`${apiUrl}/api/ris/reports`, {
          method: 'POST',
          headers: cabeceras(),
          body: JSON.stringify({ studyInstanceUid: instanceUid, ...(orderId ? { orderId } : {}), status: 'SIGNED', contentHtml: '', radiologist: medico }),
        }).catch(() => {});
      }
      toast.success(result.message || 'Archivo subido');
      setArchivo(null);
      if (inputArchivoRef.current) inputArchivoRef.current.value = '';
      fetchExistingFiles();
      onSuccess();
    } catch (e) {
      toast.error('Error al subir el archivo: ' + e.message);
    } finally {
      setSubiendo(false);
    }
  };

  const borrarArchivo = filename =>
    setConfirmar({
      titulo: 'Eliminar archivo',
      texto: `Se eliminará "${filename}". Esta acción no se puede deshacer.`,
      boton: 'Eliminar',
      accion: async () => {
        const res = await fetch(`${apiUrl}/api/subida/files/${encodeURIComponent(filename)}`, { method: 'DELETE' }).catch(() => null);
        if (res?.ok) toast.success('Archivo eliminado');
        else toast.error('No se pudo eliminar el archivo');
        fetchExistingFiles();
      },
    });

  if (!studyMeta) return null;

  // ── Vista temporal (comparar estudios): solo lectura de archivos ──
  if (temporalView) {
    return existingFiles.length > 0 ? (
      <div className="flex flex-col gap-1">
        {existingFiles.map((f: any) => (
          <a
            key={f.filename}
            className="flex w-full items-center justify-between gap-2 rounded bg-yellow-600 p-2 text-xs text-white hover:bg-yellow-700"
            href={`${apiUrl}/api/subida/files/download/${encodeURIComponent(f.filename)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {f.filename}
          </a>
        ))}
      </div>
    ) : (
      <button className="mt-3 flex cursor-not-allowed rounded bg-gray-600 p-2 text-white" disabled>
        Reporte no disponible
      </button>
    );
  }

  const estado = firmado ? 'firmado' : report ? 'borrador' : 'nuevo';
  const colorBoton = firmado ? '#22c55e' : report ? '#f59e0b' : '#38bdf8';
  const textoEstado = { firmado: 'Firmado', borrador: 'Borrador', nuevo: 'Sin informe' }[estado];
  const palabras = textoPlano(contenidoHtml) ? textoPlano(contenidoHtml).split(/\s+/).length : 0;

  return (
    <>
      <style>{CSS}</style>
      <button className="ei-boton" style={{ '--c': colorBoton } as React.CSSProperties} onClick={() => setIsOpen(true)} title={`Abrir la estación de informe (${textoEstado})`}>
        <span className="t">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V5.5L8.5 0zm4.5 1.5 4 4h-3a1 1 0 0 1-1-1zM5 8h6v1H5zm0 2h6v1H5zm0 2h4v1H5z" />
          </svg>
          Informe {existingFiles.length > 0 ? `· ${existingFiles.length} arch.` : ''}
        </span>
        <small>{textoEstado}</small>
      </button>

      {isOpen && (
        <div className="ei-fondo" onMouseDown={e => e.target === e.currentTarget && cerrar()}>
          <div className="ei-ventana" style={{ position: 'relative' }}>
            <div className="ei-top">
              <div>
                <h2>{d.paciente}</h2>
                <div className="sub">
                  {[d.modalidades.join('/'), d.estudio, d.fecha].filter(Boolean).join(' · ')}
                </div>
              </div>
              <span className={`ei-pill ${estado}`}>{estado === 'nuevo' ? 'Sin informe' : estado}</span>
              <div className="ei-tabs">
                <button className={`ei-tab ${pestana === 'informe' ? 'on' : ''}`} onClick={() => setPestana('informe')}>
                  Informe
                </button>
                <button className={`ei-tab ${pestana === 'archivos' ? 'on' : ''}`} onClick={() => setPestana('archivos')}>
                  Archivos {existingFiles.length ? `(${existingFiles.length})` : ''}
                </button>
              </div>
              <button className="ei-x" onClick={cerrar} title="Cerrar (Esc)">
                ✕
              </button>
            </div>

            {pestana === 'informe' ? (
              <>
                <div className="ei-cuerpo">
                  {/* Paciente, plantillas y ayuda de dictado */}
                  <div className="ei-col ei-izq">
                    <div className="ei-card">
                      <div className="ei-label">Paciente</div>
                      <div className="ei-pac">{d.paciente}</div>
                      <div style={{ marginTop: 8 }}>
                        {[
                          ['ID', d.documento],
                          ['Edad', d.edad],
                          ['Sexo', d.sexo],
                          ['Solicitante', d.solicitante],
                          ['N.º orden', d.accession],
                          ['Series', d.series ? (d.imagenes ? `${d.series} · ${d.imagenes} imág.` : `${d.series} series`) : ''],
                          ['Equipo', d.equipo],
                        ]
                          .filter(([, v]) => v)
                          .map(([k, v]) => (
                            <div key={k} className="ei-dato">
                              <span>{k}</span>
                              <b>{v}</b>
                            </div>
                          ))}
                      </div>
                    </div>

                    {editable && (
                      <div className="ei-card">
                        <div className="ei-label">
                          <span>Plantillas</span>
                          {d.modalidades.length > 0 && (
                            <button className="ei-link" onClick={() => setTodas(!todas)}>
                              {todas ? `Solo ${d.modalidades.join('/')}` : 'Ver todas'}
                            </button>
                          )}
                        </div>
                        <input className="ei-input" placeholder="Buscar plantilla…" value={busqueda} onChange={e => setBusqueda(e.target.value)} />
                        <div className="ei-tpls">
                          {plantillasVisibles.length === 0 ? (
                            <div className="ei-ayuda">{templates.length ? 'Ninguna coincide.' : 'No hay plantillas cargadas.'}</div>
                          ) : (
                            plantillasVisibles.map(t => (
                              <button
                                key={t._id}
                                className={`ei-tpl ${coincideConEstudio(t, d.estudio) ? 'sugerida' : ''}`}
                                onClick={() => aplicarPlantilla(t)}
                                title={coincideConEstudio(t, d.estudio) ? 'Coincide con el estudio' : 'Usar esta plantilla'}
                              >
                                <span>{t.name}</span>
                                <small>{coincideConEstudio(t, d.estudio) ? 'Sugerida' : t.modality}</small>
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    )}

                    {editable && (
                      <div className="ei-card ei-ayuda">
                        <div className="ei-label">Dictado por voz</div>
                        Pulse 🎙 en una sección o <kbd>Ctrl</kbd>+<kbd>Espacio</kbd>. Puede decir:
                        <br />«punto», «coma», «dos puntos», «nuevo párrafo»,
                        <br />«siguiente sección», «conclusiones»,
                        <br />«borrar eso», «detener dictado».
                        <br />
                        <kbd>Ctrl</kbd>+<kbd>S</kbd> guarda el borrador.
                      </div>
                    )}
                  </div>

                  {/* Editor por secciones */}
                  <div className="ei-col ei-centro">
                    {cargando ? (
                      <div className="ei-ayuda" style={{ textAlign: 'center', padding: 40 }}>
                        Cargando informe…
                      </div>
                    ) : errorCarga ? (
                      <div className="ei-card" style={{ textAlign: 'center', padding: 28 }}>
                        <div style={{ color: '#fca5a5', marginBottom: 6 }}>No se pudo cargar el informe.</div>
                        <div className="ei-ayuda" style={{ marginBottom: 12 }}>
                          {errorCarga}
                        </div>
                        <button className="ei-btn sec" onClick={cargarInforme}>
                          Reintentar
                        </button>
                      </div>
                    ) : (
                      <>
                        {editable && (
                          <div className="ei-barra">
                            <span className="ei-grupo">
                              <button className="ei-tb" title="Negrita (Ctrl+B)" onClick={() => formato('bold')}>
                                <b>B</b>
                              </button>
                              <button className="ei-tb" title="Cursiva (Ctrl+I)" onClick={() => formato('italic')}>
                                <i style={{ fontFamily: 'Georgia, serif' }}>I</i>
                              </button>
                              <button className="ei-tb" title="Subrayado (Ctrl+U)" onClick={() => formato('underline')}>
                                <u>U</u>
                              </button>
                            </span>
                            <span className="ei-grupo">
                              <button className="ei-tb" title="Lista con viñetas" onClick={() => formato('insertUnorderedList')}>
                                ☰ Viñetas
                              </button>
                              <button className="ei-tb" title="Lista numerada" onClick={() => formato('insertOrderedList')}>
                                1. Numerada
                              </button>
                            </span>
                            <span className="ei-grupo">
                              <button className="ei-tb" title="Deshacer (Ctrl+Z)" onClick={() => formato('undo')}>
                                ↶ Deshacer
                              </button>
                              <button className="ei-tb" title="Quitar negrita, cursiva y demás formato" onClick={() => formato('removeFormat')}>
                                ⌫ Limpiar
                              </button>
                            </span>
                          </div>
                        )}
                        {firmado && (
                          <div className="ei-card" style={{ borderColor: 'rgba(34,197,94,.35)', color: '#86efac', fontSize: 13 }}>
                            ✓ Firmado por <b>{report?.radiologist || 'el médico'}</b>
                            {report?.signedAt && ` el ${new Date(report.signedAt).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' })}`}.
                            Para corregirlo, vuélvalo a borrador.
                          </div>
                        )}
                        {SECCIONES.map((s, i) => (
                          <div
                            key={s.id}
                            className={`ei-sec ${dictando === s.id ? 'dictando' : activa === s.id && editable ? 'activa' : ''}`}
                            style={{ '--sc': COLOR_SECCION[s.id] } as React.CSSProperties}
                            onMouseDown={() => setActiva(s.id)}
                          >
                            <div className="ei-sec-head">
                              <span className="num">{i + 1}</span>
                              <h3>{s.titulo}</h3>
                              {editable && (
                                <button
                                  className={`ei-mic ${dictando === s.id ? 'on' : ''}`}
                                  onClick={() => (dictando === s.id ? detenerDictado() : iniciarDictado(s.id))}
                                  title={dictando === s.id ? 'Detener dictado' : 'Dictar en esta sección'}
                                >
                                  {dictando === s.id ? (
                                    <>
                                      <i /> Escuchando… detener
                                    </>
                                  ) : (
                                    <>🎙 Dictar</>
                                  )}
                                </button>
                              )}
                            </div>
                            <div
                              ref={el => (editoresRef.current[s.id] = el)}
                              className="ei-edit"
                              contentEditable={editable}
                              suppressContentEditableWarning
                              spellCheck
                              lang="es"
                              data-placeholder={editable ? s.ayuda : '—'}
                              onFocus={() => setActiva(s.id)}
                              onInput={() => alEditar(s.id)}
                              onPaste={alPegar}
                            />
                            {dictando === s.id && enVivo && <div className="ei-vivo">{enVivo}…</div>}
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Hoja en vivo */}
                  <div className="ei-col ei-der">
                    <h4>
                      <span>Así se verá el informe impreso</span>
                      <span className="ei-a4">A4</span>
                    </h4>
                    <div className="ei-hoja-wrap">
                      <iframe
                        className="ei-hoja"
                        title="Vista de la hoja del informe"
                        srcDoc={hojaHtml(datosHoja())}
                        scrolling="no"
                        onLoad={e => {
                          // La hoja crece con el texto: así hay una sola barra (la de la columna)
                          const f = e.currentTarget;
                          const alto = f.contentDocument?.documentElement?.scrollHeight || 0;
                          f.style.height = `${Math.max(alto, f.clientWidth * 1.414)}px`;
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="ei-pie">
                  <span className={`meta ${sucio ? 'sucio' : ''}`}>
                    {sucio
                      ? '● Cambios sin guardar'
                      : report?.updatedAt
                        ? `Guardado ${new Date(report.updatedAt).toLocaleString('es-BO', { dateStyle: 'short', timeStyle: 'short' })}`
                        : 'Informe nuevo'}
                    {' · '}
                    {palabras} palabras
                  </span>
                  <span style={{ flex: 1 }} />
                  <button className="ei-btn sec" onClick={imprimir} disabled={!!errorCarga}>
                    🖨 Vista previa / PDF
                  </button>
                  {editable && (
                    <>
                      <button className="ei-btn sec" onClick={() => guardar('DRAFT')} disabled={guardando || !!errorCarga}>
                        {guardando ? 'Guardando…' : 'Guardar borrador'}
                      </button>
                      <button
                        className="ei-btn sec"
                        disabled={!!errorCarga}
                        title="Busca campos sin completar, incoherencias, hallazgos críticos y guías de seguimiento"
                        onClick={() => abrirRevision(false)}
                      >
                        🔎 Revisar
                      </button>
                      <button
                        className="ei-btn pri"
                        disabled={guardando || !!errorCarga}
                        onClick={() => abrirRevision(true)}
                      >
                        ✓ Aprobar y firmar
                      </button>
                    </>
                  )}
                  {firmado && puedeInformar && (
                    <button
                      className="ei-btn peligro"
                      onClick={() =>
                        setConfirmar({
                          titulo: 'Volver a borrador',
                          texto: 'El informe dejará de estar firmado y se podrá editar. Tendrá que firmarlo de nuevo.',
                          boton: 'Volver a borrador',
                          accion: () => guardar('DRAFT', true),
                        })
                      }
                    >
                      Volver a borrador
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="ei-col" style={{ flex: 1 }}>
                <div className="ei-archivos">
                  <div className="ei-ayuda">
                    Suba aquí el PDF o la imagen de un informe hecho fuera del sistema. Los archivos quedan guardados a nombre de{' '}
                    <b style={{ color: '#e6f4f3' }}>{nombreArchivos || 'el paciente'}</b>.
                  </div>
                  {puedeInformar && (
                    <>
                      <label className="ei-drop">
                        <input
                          ref={inputArchivoRef}
                          type="file"
                          accept=".pdf,image/*,.doc,.docx"
                          hidden
                          onChange={e => setArchivo(e.target.files?.[0] || null)}
                        />
                        {archivo ? (
                          <>
                            <div style={{ fontSize: 26 }}>📄</div>
                            <b style={{ color: '#fff' }}>{archivo.name}</b>
                            <div>{(archivo.size / 1024).toFixed(0)} KB · haga clic para cambiarlo</div>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 26 }}>⬆</div>
                            Haga clic para elegir un archivo (PDF, imagen o Word)
                          </>
                        )}
                      </label>
                      <button className="ei-btn pri" onClick={subirArchivo} disabled={!archivo || subiendo}>
                        {subiendo ? 'Subiendo…' : 'Subir archivo'}
                      </button>
                    </>
                  )}
                  <div className="ei-label" style={{ marginTop: 6 }}>
                    Archivos del paciente
                  </div>
                  {loadingFiles ? (
                    <div className="ei-ayuda">Cargando…</div>
                  ) : existingFiles.length === 0 ? (
                    <div className="ei-ayuda">Este paciente todavía no tiene archivos.</div>
                  ) : (
                    existingFiles.map((f: any) => (
                      <div key={f.filename} className="ei-fila-arch">
                        <span>📄 {f.filename}</span>
                        <small style={{ color: '#8fbcbc' }}>{f.size ? `${(f.size / 1024).toFixed(0)} KB` : ''}</small>
                        <a
                          className="ei-mini"
                          style={{ background: '#1d4ed8', color: '#fff', textDecoration: 'none' }}
                          href={`${apiUrl}/api/subida/files/download/${encodeURIComponent(f.filename)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Abrir
                        </a>
                        {rolUsuario === 'admin' && (
                          <button className="ei-mini" style={{ background: '#7f1d1d', color: '#fecaca' }} onClick={() => borrarArchivo(f.filename)}>
                            Eliminar
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {confirmar && (
              <div className="ei-confirm" onMouseDown={() => setConfirmar(null)}>
                <div onMouseDown={e => e.stopPropagation()} className={confirmar.lista ? 'ancho' : ''}>
                  <h3>{confirmar.titulo}</h3>
                  <p>{confirmar.texto}</p>
                  {confirmar.nota && <p className="ei-rev-ok">{confirmar.nota}</p>}
                  {confirmar.lista?.length > 0 && (
                    <ul className="ei-rev">
                      {confirmar.lista.map((o, i) => (
                        <li key={i} className={o.tipo} onClick={() => irASeccion(o.seccion)} title="Ir a la sección">
                          <b>{{ critico: 'Crítico', error: 'Revisar', aviso: 'Atención', guia: 'Guía' }[o.tipo]}</b>
                          <span>{o.texto}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="ei-btn sec" onClick={() => setConfirmar(null)}>
                      {confirmar.cancelar || 'Cancelar'}
                    </button>
                    {confirmar.secundario && (
                      <button
                        className="ei-btn peligro"
                        onClick={() => {
                          confirmar.accionSecundaria();
                          setConfirmar(null);
                        }}
                      >
                        {confirmar.secundario}
                      </button>
                    )}
                    {confirmar.boton && (
                      <button
                        className="ei-btn pri"
                        onClick={() => {
                          const c = confirmar;
                          setConfirmar(null);
                          c.accion();
                        }}
                      >
                        {confirmar.boton}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default UploadDocuments;
